import debounce from 'lodash/fp/debounce';
import head from 'lodash/fp/head';
import isEmpty from 'lodash/fp/isEmpty';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';

import { rawDataLayerPush } from '../configuration/setup/googleTagManager';
import {
    useActiveAssetId,
    useActiveChargingStationId,
    useActiveGeofenceId,
    useActivePoiId,
    useActiveSimplePayPoiId,
    useSelectedAssetGroupIds,
    useSelectedAssetIds,
    useSelectedDriverIds,
} from '../features/app/appHooks';
import { useIsShareLinkDialogShown } from '../features/dialogs/shareLinkDialog/shareLinkSlice';
import {
    useCenter,
    useHasLockOnAsset,
    useMapLayers,
    useMapType,
    useShowChargingStations,
    useShowCluster,
    useShowCustomerPois,
    useShowGeofences,
    useShowSimplePayPois,
    useShowWorkshopPois,
    useZoom,
} from '../features/map/mapHooks';
import { useIsServiceInfoDialogShown, useServiceInfoDialogChapter } from '../features/serviceInfo/serviceInfoSelectors';
import {
    useActiveTab,
    useChunkCounter,
    useShowTableSettingsDialog,
    useViewType,
} from '../features/sidebar/sidebarHooks';
import {
    useActivityFilter,
    useAssetFilter,
    useColumns,
    useNotificationFilter,
    useSearchValue,
    useSortBy,
    useSortDir,
} from '../features/table/tableHooks';
import {
    useAssetDisplayNameType,
    useIsTreeOpen,
    useShowAssetGroups,
    useShowDriverGroups,
    useShowEmptyGroups,
    useShowFuelType,
    useTreeCategory,
    useTreePoiCategory,
    useTreeRoutePlanningCategory,
} from '../features/tree/treeHooks';
import { usePreviousLocation } from '../hooks/usePreviousLocation';
import { isMapView, type MakeRouteProp, makeRoute } from './routeIntents';
import { getBasePath } from './routes';

const PUSH_ROUTE_DEBOUNCE = 200;

const useCommonRouteProps = () => ({
    activeAssetId: useActiveAssetId(),
    tabId: useActiveTab(),
    showServiceInfoDialog: useIsServiceInfoDialogShown(),
    serviceInfoChapter: useServiceInfoDialogChapter(),
    showShareLinkDialog: useIsShareLinkDialogShown(),
    selectedAssetGroupIds: useSelectedAssetGroupIds(),
});

const useMapViewRouteProps = () => {
    const commonProps = useCommonRouteProps();

    const activePoiId = useActivePoiId();
    const activeChargingStationId = useActiveChargingStationId();
    const activeSimplePayPoi = useActiveSimplePayPoiId();

    return {
        ...commonProps,
        selectedAssetIds: useSelectedAssetIds(),
        selectedDriverIds: useSelectedDriverIds(),
        treeCategory: useTreeCategory(),
        treePoiCategory: useTreePoiCategory(),
        treeRoutePlanningCategory: useTreeRoutePlanningCategory(),
        isTreeOpen: useIsTreeOpen(),
        areEmptyGroupsShown: useShowEmptyGroups(),
        areAssetGroupsShown: useShowAssetGroups(),
        areDriverGroupsShown: useShowDriverGroups(),
        isFuelTypeShown: useShowFuelType(),
        center: useCenter(),
        zoom: useZoom(),
        mapType: useMapType(),
        mapLayers: useMapLayers(),
        showCluster: useShowCluster(),
        showWorkshopPois: useShowWorkshopPois(),
        showCustomerPois: useShowCustomerPois(),
        showSimplePayPois: useShowSimplePayPois(),
        showGeofences: useShowGeofences(),
        showChargingStations: useShowChargingStations(),
        lockOnAsset: useHasLockOnAsset(),
        assetDisplayNameType: useAssetDisplayNameType(),
        geofenceId: useActiveGeofenceId(),
        poiId: activePoiId,
        cPoiId: activeChargingStationId,
        sPoiId: activeSimplePayPoi,
    } as MakeRouteProp;
};

const useTableViewRouteProps = () => {
    const commonProps = useCommonRouteProps();
    return {
        ...commonProps,
        tableSearch: useSearchValue(),
        sortBy: useSortBy(),
        sortDir: useSortDir(),
        columns: useColumns(),
        activityFilter: useActivityFilter(),
        assetFilter: useAssetFilter(),
        notificationFilter: useNotificationFilter(),
        chunks: useChunkCounter(),
        viewType: useViewType(),
        showTableSettings: useShowTableSettingsDialog(),
    } as MakeRouteProp;
};

// Check if location is list or map and return path and search queries accordingly. Note, this could
// lead to a redirect if check fails.
const useRouteFittingToState = () => {
    const mapRouteProps = useMapViewRouteProps();
    const tableRouteProps = useTableViewRouteProps();
    return isMapView() ? makeRoute(mapRouteProps) : makeRoute(tableRouteProps);
};

export const getFirstLevelOfUrl = (path: string) => head(path.match(/\/\w*/));

export const RouterUpdater = () => {
    const [lastPath, setLastPath] = useState<string | undefined>(getBasePath());
    const navigate = useNavigate();
    const location = useLocation();
    const prevLocation = usePreviousLocation(location);

    const routeToPushTo = useRouteFittingToState();

    // Use memo to ensure debounce function is stable on each render cycle
    const pushRoute = useMemo(() => debounce(PUSH_ROUTE_DEBOUNCE)((route: string) => navigate(route)), [navigate]);

    useEffect(() => () => pushRoute.cancel(), [location, pushRoute]);

    const handleRouteUpdates = useCallback(() => {
        if (prevLocation && prevLocation.key !== location.key) {
            return;
        }

        const currentRoute = `${location.pathname}${location.search}`;
        const newBasePath = getFirstLevelOfUrl(routeToPushTo);

        if (!prevLocation && !isEmpty(location.search)) {
            // Avoid loop on first load
            return;
        }

        if (routeToPushTo !== currentRoute) {
            if (lastPath !== newBasePath) {
                // Change main view
                navigate(routeToPushTo, { replace: true });
                setLastPath(newBasePath);
                rawDataLayerPush({
                    event: 'virtPath',
                    virtPath: newBasePath,
                });
            } else {
                pushRoute(routeToPushTo);
            }
        }
    }, [location, routeToPushTo, lastPath, prevLocation, pushRoute, navigate]);

    useEffect(() => {
        handleRouteUpdates();
    }, [routeToPushTo]);

    return null;
};
